/**
 * Sysadmin Settings rail — TanStack Query hooks for PR-C (Phase 5).
 *
 * Backs the 3 v1 sub-tabs:
 *   - /sysadmin/settings/idp           → IdP defaults CRUD
 *   - /sysadmin/settings/notifications → channels + rules CRUD
 *   - /sysadmin/settings/api           → API keys list + create + revoke
 *
 * Built on top of `createSysadminQuery` + `createSysadminMutation` from
 * use-sysadmin-query.ts (PR-0 + PR-B). Schemas live HERE (vs duplicated
 * across routes) so the wire types stay in lockstep with the Fastify
 * route schemas.
 */

import { z } from 'zod';
import {
  createSysadminMutation,
  createSysadminQuery,
} from './use-sysadmin-query';

// ════════════════════════════════════════════════════════════════════════
//  IdP defaults
// ════════════════════════════════════════════════════════════════════════

export const IdpProtocolSchema = z.enum(['oidc', 'saml', 'google', 'entra']);
export type IdpProtocol = z.infer<typeof IdpProtocolSchema>;

export const IdpDefaultRowSchema = z.object({
  id: z.string().uuid(),
  protocol: IdpProtocolSchema,
  name: z.string(),
  config: z.record(z.string(), z.unknown()),
  isEnabled: z.boolean(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type IdpDefaultRow = z.infer<typeof IdpDefaultRowSchema>;

export const IdpListResponseSchema = z.object({
  items: z.array(IdpDefaultRowSchema),
  total: z.number().int(),
});
export type IdpListResponse = z.infer<typeof IdpListResponseSchema>;

export const useSysadminIdpDefaults = createSysadminQuery({
  key: ['sysadmin-settings-idp'],
  url: '/admin/platform/settings/idp',
  schema: IdpListResponseSchema,
});

export const IdpCreateInputSchema = z.object({
  protocol: IdpProtocolSchema,
  name: z.string().min(1).max(200),
  config: z.record(z.string(), z.unknown()).default({}),
  isEnabled: z.boolean().default(false),
});
export type IdpCreateInput = z.infer<typeof IdpCreateInputSchema>;

export const useCreateIdpDefault = createSysadminMutation<
  IdpCreateInput,
  IdpDefaultRow
>({
  url: '/admin/platform/settings/idp',
  method: 'POST',
  inputSchema: IdpCreateInputSchema,
  outputSchema: IdpDefaultRowSchema,
  invalidates: [['sysadmin-settings-idp']],
});

export const IdpUpdateInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  isEnabled: z.boolean().optional(),
});
export type IdpUpdateInput = z.infer<typeof IdpUpdateInputSchema>;

// The body sent to the API excludes the id (it's in the URL).
const IdpUpdateBodySchema = z.object({
  name: z.string().min(1).max(200).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  isEnabled: z.boolean().optional(),
});

export const useUpdateIdpDefault = createSysadminMutation<
  IdpUpdateInput,
  IdpDefaultRow
>({
  url: ({ id }) => `/admin/platform/settings/idp/${id}`,
  method: 'PATCH',
  // The factory ships the full input as the body; the route ignores `id`
  // since it's in the URL. Body-only schema is enforced via inputSchema
  // after we strip the id below. Simplest: pass the full body but let
  // Zod validate the relevant fields.
  inputSchema: IdpUpdateInputSchema,
  outputSchema: IdpDefaultRowSchema,
  invalidates: [['sysadmin-settings-idp']],
});
void IdpUpdateBodySchema; // referenced by future codegen

const IdpDeleteInputSchema = z.object({
  id: z.string().uuid(),
});
export type IdpDeleteInput = z.infer<typeof IdpDeleteInputSchema>;

const IdpDeleteOutputSchema = z.object({
  success: z.literal(true),
  deletedId: z.string().uuid(),
});

export const useDeleteIdpDefault = createSysadminMutation<
  IdpDeleteInput,
  z.infer<typeof IdpDeleteOutputSchema>
>({
  url: ({ id }) => `/admin/platform/settings/idp/${id}`,
  method: 'DELETE',
  inputSchema: IdpDeleteInputSchema,
  outputSchema: IdpDeleteOutputSchema,
  invalidates: [['sysadmin-settings-idp']],
});

// ════════════════════════════════════════════════════════════════════════
//  Notifications — channels
// ════════════════════════════════════════════════════════════════════════

export const ChannelTypeSchema = z.enum([
  'email',
  'slack',
  'pagerduty',
  'webhook',
  'sms',
  'in_app',
]);
export type ChannelType = z.infer<typeof ChannelTypeSchema>;

export const PlatformChannelRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  channelType: ChannelTypeSchema,
  config: z.record(z.string(), z.unknown()),
  isEnabled: z.boolean(),
  isPlatformLevel: z.literal(true),
  lastTestAt: z.string().nullable(),
  lastTestOutcome: z.enum(['success', 'failure']).nullable(),
  lastTestError: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PlatformChannelRow = z.infer<typeof PlatformChannelRowSchema>;

export const PlatformChannelsListSchema = z.object({
  channels: z.array(PlatformChannelRowSchema),
});

export const useSysadminPlatformChannels = createSysadminQuery({
  key: ['sysadmin-settings-notifications-channels'],
  url: '/admin/platform/settings/notifications/channels',
  schema: PlatformChannelsListSchema,
});

export const ChannelCreateInputSchema = z.object({
  name: z.string().min(1).max(120),
  channelType: ChannelTypeSchema,
  config: z.record(z.string(), z.unknown()).default({}),
  isEnabled: z.boolean().default(true),
});
export type ChannelCreateInput = z.infer<typeof ChannelCreateInputSchema>;

export const useCreatePlatformChannel = createSysadminMutation<
  ChannelCreateInput,
  PlatformChannelRow
>({
  url: '/admin/platform/settings/notifications/channels',
  method: 'POST',
  inputSchema: ChannelCreateInputSchema,
  outputSchema: PlatformChannelRowSchema,
  invalidates: [['sysadmin-settings-notifications-channels']],
});

export const ChannelUpdateInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(120).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  isEnabled: z.boolean().optional(),
});
export type ChannelUpdateInput = z.infer<typeof ChannelUpdateInputSchema>;

export const useUpdatePlatformChannel = createSysadminMutation<
  ChannelUpdateInput,
  PlatformChannelRow
>({
  url: ({ id }) => `/admin/platform/settings/notifications/channels/${id}`,
  method: 'PATCH',
  inputSchema: ChannelUpdateInputSchema,
  outputSchema: PlatformChannelRowSchema,
  invalidates: [['sysadmin-settings-notifications-channels']],
});

const ChannelDeleteInputSchema = z.object({ id: z.string().uuid() });
export type ChannelDeleteInput = z.infer<typeof ChannelDeleteInputSchema>;

export const useDeletePlatformChannel = createSysadminMutation<
  ChannelDeleteInput,
  { success: true }
>({
  url: ({ id }) => `/admin/platform/settings/notifications/channels/${id}`,
  method: 'DELETE',
  inputSchema: ChannelDeleteInputSchema,
  outputSchema: z.object({ success: z.literal(true) }),
  invalidates: [
    ['sysadmin-settings-notifications-channels'],
    ['sysadmin-settings-notifications-rules'],
  ],
});

// ════════════════════════════════════════════════════════════════════════
//  Notifications — rules
// ════════════════════════════════════════════════════════════════════════

export const SeveritySchema = z.enum([
  'critical',
  'high',
  'medium',
  'low',
  'info',
]);
export type Severity = z.infer<typeof SeveritySchema>;

// #1135 — Health hint surfaced by the API on every rule read. The
// fields are derived from row state on the server (NOT persisted),
// so they always reflect the live state. Tolerate older API builds
// by accepting absent fields and defaulting to "healthy".
export const PlatformRuleHealthIssueSchema = z.enum([
  'no_channels_when_enabled',
]);

export const PlatformRuleRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  eventPattern: z.string(),
  severityFilter: z.array(z.string()),
  channelIds: z.array(z.string().uuid()),
  rateLimitPerHour: z.number().int().nullable(),
  isEnabled: z.boolean(),
  isPlatformLevel: z.literal(true),
  isHealthy: z.boolean().default(true),
  healthIssues: z.array(PlatformRuleHealthIssueSchema).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PlatformRuleRow = z.infer<typeof PlatformRuleRowSchema>;

export const PlatformRulesListSchema = z.object({
  rules: z.array(PlatformRuleRowSchema),
});

export const useSysadminPlatformRules = createSysadminQuery({
  key: ['sysadmin-settings-notifications-rules'],
  url: '/admin/platform/settings/notifications/rules',
  schema: PlatformRulesListSchema,
});

export const RuleCreateInputSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  eventPattern: z.string().min(1).max(200),
  severityFilter: z.array(SeveritySchema).default([]),
  channelIds: z.array(z.string().uuid()).default([]),
  rateLimitPerHour: z.number().int().min(1).max(100000).nullable().optional(),
  isEnabled: z.boolean().default(true),
});
export type RuleCreateInput = z.infer<typeof RuleCreateInputSchema>;

export const useCreatePlatformRule = createSysadminMutation<
  RuleCreateInput,
  PlatformRuleRow
>({
  url: '/admin/platform/settings/notifications/rules',
  method: 'POST',
  inputSchema: RuleCreateInputSchema,
  outputSchema: PlatformRuleRowSchema,
  invalidates: [['sysadmin-settings-notifications-rules']],
});

export const RuleUpdateInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).nullable().optional(),
  eventPattern: z.string().min(1).max(200).optional(),
  severityFilter: z.array(SeveritySchema).optional(),
  channelIds: z.array(z.string().uuid()).optional(),
  rateLimitPerHour: z.number().int().min(1).max(100000).nullable().optional(),
  isEnabled: z.boolean().optional(),
});
export type RuleUpdateInput = z.infer<typeof RuleUpdateInputSchema>;

export const useUpdatePlatformRule = createSysadminMutation<
  RuleUpdateInput,
  PlatformRuleRow
>({
  url: ({ id }) => `/admin/platform/settings/notifications/rules/${id}`,
  method: 'PATCH',
  inputSchema: RuleUpdateInputSchema,
  outputSchema: PlatformRuleRowSchema,
  invalidates: [['sysadmin-settings-notifications-rules']],
});

const RuleDeleteInputSchema = z.object({ id: z.string().uuid() });
export type RuleDeleteInput = z.infer<typeof RuleDeleteInputSchema>;

export const useDeletePlatformRule = createSysadminMutation<
  RuleDeleteInput,
  { success: true }
>({
  url: ({ id }) => `/admin/platform/settings/notifications/rules/${id}`,
  method: 'DELETE',
  inputSchema: RuleDeleteInputSchema,
  outputSchema: z.object({ success: z.literal(true) }),
  invalidates: [['sysadmin-settings-notifications-rules']],
});

// ════════════════════════════════════════════════════════════════════════
//  API keys
// ════════════════════════════════════════════════════════════════════════

export const ApiKeyScopeSchema = z.enum(['read', 'write']);
export type ApiKeyScope = z.infer<typeof ApiKeyScopeSchema>;

export const PlatformApiKeyRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  scope: ApiKeyScopeSchema,
  expiresAt: z.string(),
  lastUsedAt: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
  status: z.enum(['active', 'expired', 'revoked']),
});
export type PlatformApiKeyRow = z.infer<typeof PlatformApiKeyRowSchema>;

export const PlatformApiKeysListSchema = z.object({
  items: z.array(PlatformApiKeyRowSchema),
  total: z.number().int(),
});

export const useSysadminPlatformApiKeys = createSysadminQuery({
  key: ['sysadmin-settings-api-keys'],
  url: '/admin/platform/settings/api-keys',
  schema: PlatformApiKeysListSchema,
});

export const ApiKeyCreateInputSchema = z.object({
  name: z.string().min(1).max(200),
  scope: ApiKeyScopeSchema,
  expiresInDays: z.number().int().min(1).max(365),
});
export type ApiKeyCreateInput = z.infer<typeof ApiKeyCreateInputSchema>;

export const ApiKeyCreateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  scope: ApiKeyScopeSchema,
  /**
   * Plaintext secret — returned ONCE. The UI surfaces a modal with a
   * Copy button and a warning that the key will not be shown again.
   */
  value: z.string(),
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type ApiKeyCreateResponse = z.infer<typeof ApiKeyCreateResponseSchema>;

export const useCreatePlatformApiKey = createSysadminMutation<
  ApiKeyCreateInput,
  ApiKeyCreateResponse
>({
  url: '/admin/platform/settings/api-keys',
  method: 'POST',
  inputSchema: ApiKeyCreateInputSchema,
  outputSchema: ApiKeyCreateResponseSchema,
  invalidates: [['sysadmin-settings-api-keys']],
});

export const ApiKeyRevokeInputSchema = z.object({
  id: z.string().uuid(),
  confirmPhrase: z.string().optional(),
});
export type ApiKeyRevokeInput = z.infer<typeof ApiKeyRevokeInputSchema>;

const ApiKeyRevokeOutputSchema = z.object({
  success: z.literal(true),
  revokedId: z.string().uuid(),
  revokedAt: z.string(),
});

export const useRevokePlatformApiKey = createSysadminMutation<
  ApiKeyRevokeInput,
  z.infer<typeof ApiKeyRevokeOutputSchema>
>({
  url: ({ id }) => `/admin/platform/settings/api-keys/${id}`,
  method: 'DELETE',
  inputSchema: ApiKeyRevokeInputSchema,
  outputSchema: ApiKeyRevokeOutputSchema,
  invalidates: [['sysadmin-settings-api-keys']],
});

// ════════════════════════════════════════════════════════════════════════
//  Email — Platform SMTP transport (F-W1a-03.1 / F-W1a-06.2)
// ════════════════════════════════════════════════════════════════════════

export const EmailSecureSchema = z.enum(['tls', 'starttls', 'none']);
export type EmailSecure = z.infer<typeof EmailSecureSchema>;

export const EmailDeliveryModeSchema = z.enum([
  'required',
  'best_effort',
  'disabled',
]);
export type EmailDeliveryMode = z.infer<typeof EmailDeliveryModeSchema>;

export const PlatformEmailConfigSchema = z.object({
  enabled: z.boolean(),
  deliveryMode: EmailDeliveryModeSchema,
  host: z.string().nullable(),
  port: z.number().int(),
  username: z.string().nullable(),
  /** TRUE when sysadmin has set a password (never returned plaintext). */
  passwordConfigured: z.boolean(),
  secure: EmailSecureSchema,
  fromAddress: z.string().nullable(),
  fromName: z.string().nullable(),
  replyTo: z.string().nullable(),
});
export type PlatformEmailConfig = z.infer<typeof PlatformEmailConfigSchema>;

export const useSysadminPlatformEmail = createSysadminQuery({
  key: ['sysadmin-settings-email'],
  url: '/admin/platform/settings/email',
  schema: PlatformEmailConfigSchema,
});

export const PlatformEmailUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  deliveryMode: EmailDeliveryModeSchema.optional(),
  host: z.string().min(1).max(255).nullable().optional(),
  port: z.number().int().min(1).max(65535).optional(),
  username: z.string().max(255).nullable().optional(),
  /**
   * Write-only.  Omit to preserve, '' to clear, any other value
   * encrypts on the server.  Never echoed back.
   */
  password: z.string().max(1024).optional(),
  secure: EmailSecureSchema.optional(),
  fromAddress: z.string().email().nullable().optional(),
  fromName: z.string().max(120).nullable().optional(),
  replyTo: z.string().email().nullable().optional(),
});
export type PlatformEmailUpdate = z.infer<typeof PlatformEmailUpdateSchema>;

export const useUpdatePlatformEmail = createSysadminMutation<
  PlatformEmailUpdate,
  PlatformEmailConfig
>({
  url: '/admin/platform/settings/email',
  method: 'PUT',
  inputSchema: PlatformEmailUpdateSchema,
  outputSchema: PlatformEmailConfigSchema,
  invalidates: [['sysadmin-settings-email']],
});

export const PlatformEmailTestInputSchema = z.object({
  to: z.string().email(),
});
export type PlatformEmailTestInput = z.infer<typeof PlatformEmailTestInputSchema>;

export const PlatformEmailTestResultSchema = z.object({
  delivery: z.enum(['sent', 'failed', 'disabled']),
  transport: z.enum(['tenant', 'platform', 'none']),
  messageId: z.string().optional(),
  errorCode: z.string().optional(),
  error: z.string().optional(),
});
export type PlatformEmailTestResult = z.infer<typeof PlatformEmailTestResultSchema>;

export const useSendPlatformEmailTest = createSysadminMutation<
  PlatformEmailTestInput,
  PlatformEmailTestResult
>({
  url: '/admin/platform/settings/email/test',
  method: 'POST',
  inputSchema: PlatformEmailTestInputSchema,
  outputSchema: PlatformEmailTestResultSchema,
  // Test sends don't change config; nothing to invalidate.
  invalidates: [],
});
